/**
 * Vehículos compatibles de un producto.
 *
 * Es una lista y no un campo porque un repuesto sirve para varios autos: en el
 * catálogo ya había un "CHEVROLET CORSA - FIAT UNO - FIAT FIORINO" metido en un
 * solo renglón de texto. Se agregan de a uno y quedan como etiquetas.
 *
 * El autocompletado sugiere las marcas y modelos que ya pasaron por el taller,
 * así "Hilux" y "Toyota Hilux" dejan de ser dos cosas distintas. Igual se puede
 * escribir uno que no esté: sugiere, no obliga.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Tip } from './ui/tip';

export interface Fitment {
  marca: string;
  modelo: string;
  desde?: number | null;
  hasta?: number | null;
}

export const etiquetaFitment = (it: Fitment) => {
  const nombre = [it.marca, it.modelo].filter(Boolean).join(' ');
  if (!it.desde && !it.hasta) return nombre;
  if (it.desde && it.hasta) return `${nombre} (${it.desde}-${it.hasta})`;
  return it.desde ? `${nombre} (${it.desde}+)` : `${nombre} (hasta ${it.hasta})`;
};

/** Acepta la lista nueva o el texto libre viejo, para no perder lo cargado. */
export const parseFitment = (raw: any): Fitment[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  const texto = String(raw).trim();
  if (!texto) return [];
  if (texto.startsWith('[')) {
    try { return JSON.parse(texto); } catch { /* texto viejo */ }
  }
  return texto.split(/\s+[-/]\s+|,/).map((t) => ({ marca: '', modelo: t.trim() })).filter((x) => x.modelo);
};

export function VehicleFitmentInput({
  value, onChange
}: { value: any; onChange: (lista: Fitment[]) => void }) {
  const lista = useMemo(() => parseFitment(value), [value]);
  const [catalogo, setCatalogo] = useState<Array<{ marca: string; modelo: string }>>([]);
  const [texto, setTexto] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [abierto, setAbierto] = useState(false);
  const cajaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    axios.get('/api/vehicles/modelos')
      .then((res) => setCatalogo(res.data.modelos || []))
      .catch(() => setCatalogo([]));
  }, []);

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (cajaRef.current && !cajaRef.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const sugerencias = useMemo(() => {
    const q = texto.trim().toLowerCase();
    if (!q) return catalogo.slice(0, 8);
    return catalogo
      .filter((c) => `${c.marca} ${c.modelo}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [texto, catalogo]);

  const agregar = (marca: string, modelo: string) => {
    if (!marca && !modelo) return;
    const nuevo: Fitment = {
      marca, modelo,
      desde: Number(desde) || null,
      hasta: Number(hasta) || null
    };
    const clave = (x: Fitment) => `${x.marca}|${x.modelo}|${x.desde || ''}|${x.hasta || ''}`.toLowerCase();
    if (lista.some((x) => clave(x) === clave(nuevo))) return;
    onChange([...lista, nuevo]);
    setTexto(''); setDesde(''); setHasta(''); setAbierto(false);
  };

  // Lo tipeado a mano: la primera palabra se toma como marca si coincide con
  // una conocida; si no, entra todo como modelo y no se pierde nada.
  const agregarEscrito = () => {
    const t = texto.trim();
    if (!t) return;
    const marcas: string[] = [];
    catalogo.forEach((c) => { if (c.marca && !marcas.includes(c.marca)) marcas.push(c.marca); });
    const marca = marcas.find((m) => t.toLowerCase().startsWith(m.toLowerCase()));
    if (marca) agregar(marca, t.slice(marca.length).trim());
    else agregar('', t);
  };

  return (
    <div className="space-y-2" ref={cajaRef}>
      {lista.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {lista.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 text-sm">
              {etiquetaFitment(it)}
              <Tip label="Quitar">
                <button
                  type="button"
                  onClick={() => onChange(lista.filter((_, j) => j !== i))}
                  className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </Tip>
            </span>
          ))}
        </div>
      )}

      <div className="relative flex items-center gap-2">
        <Input
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); agregarEscrito(); }
          }}
          placeholder="Marca y modelo, ej: Toyota Hilux"
          className="flex-1"
        />
        <Tip label="Desde qué año (opcional)">
          <Input inputMode="numeric" value={desde} onChange={(e) => setDesde(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Desde" className="w-24" />
        </Tip>
        <Tip label="Hasta qué año (opcional)">
          <Input inputMode="numeric" value={hasta} onChange={(e) => setHasta(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Hasta" className="w-24" />
        </Tip>
        <Button type="button" variant="outline" size="icon" onClick={agregarEscrito} disabled={!texto.trim()} aria-label="Agregar">
          <PlusIcon className="h-4 w-4" />
        </Button>

        {abierto && sugerencias.length > 0 && (
          <div className="absolute left-0 top-full z-50 mt-1 w-full max-w-md overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
            {sugerencias.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => agregar(c.marca, c.modelo)}
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                {c.marca} <span className="text-muted-foreground">{c.modelo}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Se pueden cargar varios. Los años son opcionales y sirven cuando la pieza cambió de una
        generación a otra.
      </p>
    </div>
  );
}

export default VehicleFitmentInput;
